import {
  type ServiceIdentityActionManagerState,
  type ServiceIdentityFilters,
  type ServiceIdentityItem,
  type ServiceIdentitySortField,
  type ServiceIdentitySortOrder,
} from '../../../shared/types';

/** Redux slice name constants for the service identity feature. */
export enum ServiceIdentityType {
  SLICE_NAME = 'savicopilot/serviceIdentitySlice',
  EP_SLICE_NAME = 'savicopilot/serviceIdentityEpSlice',
}

/** Root Redux state shape for the service identity slice, including search, filters, sort, list, side panel, and action manager. */
export interface ServiceIdentityState {
  // General
  isGlobalLoading: boolean;
  // Search
  searchText: string;
  // Filters
  isFilterBar: boolean;
  filters: ServiceIdentityFilters[];
  activeFilters: Record<
    string,
    (string | number | Record<string, string | number>)[]
  >;
  // Sort
  sort: ServiceIdentitySortField;
  order: ServiceIdentitySortOrder;
  // List
  list: {
    totalCount: number | null;
    isListLoading: boolean;
    rowsToUpdate: Record<number, Partial<ServiceIdentityItem>> | null;
  };
  // Sidepanel
  sidePanel: {
    isSidePanelOpen: boolean;
    sidePanelData: ServiceIdentityItem | null;
  };
  // Actions
  actionManager: ServiceIdentityActionManagerState | null;
}

/** Payload for fetching a paginated, filtered, and sorted identity list. */
export interface GetIdentityListProps {
  search: string;
  filter: Record<string, (string | number | Record<string, string | number>)[]>;
  offset: number;
  max: number;
  sort: ServiceIdentitySortField;
  order: ServiceIdentitySortOrder;
}

export interface GetServiceIdentityHistoryProps {
  id: number;
  cursor: string | null;
}

/** Payload for creating a new service identity. */
export interface CreateServiceIdentityProps {
  name: string;
  description: string;
  owners: { type: string; rank: number; id: number }[];
  tags: string[];
}

/** Payload for fetching a cursor-paginated owners list. */
export interface GetOwnersListProps {
  search?: string;
  type?: string;
  serviceId?: number;
  cursor?: string | null;
  max: number;
}

/** Payload for fetching a cursor-paginated password policies list. */
export interface GetPoliciesListProps {
  search?: string;
  cursor?: string | number | null;
  max: number;
}

/** Payload for updating a service identity's description, tags, or password policy. */
export interface UpdateServiceIdentityProps {
  serviceId: number;
  data: {
    description?: string | null;
    tags?: string[] | null;
    passwordPolicy?: number | null;
  };
}

/** Payload for rotating a service identity's password credential. */
export interface RotatePasswordProps {
  serviceId: number;
  policyId: number;
}

/** Payload for fetching a cursor-paginated service account list. */
export interface GetServiceAccountListProps {
  serviceId?: number;
  cursor: string | null;
  max: number;
  search?: string;
  filter?: Record<string, (string | number)[]>;
}

/** Payload for linking or unlinking service accounts from a service identity. */
export interface UpdateServiceAccountListProps {
  serviceId: number;
  addAccounts?: { accountKey: number }[];
  removeAccounts?: { accountKey: number }[];
}

/** Payload for replacing the owners list of a service identity. */
export interface UpdateOwnersListProps {
  serviceId: number;
  owners: { name: string; id: number; rank: number; type: string }[];
}

export interface GetNameListProps {
  name: string;
  max: number;
  cursor: string | null;
}

export interface GetApplicationListProps {
  wsearch?: string;
  type: string;
  max: number;
  offset: number;
}

/** Response shape returned by the identity list endpoint. */
export interface GetIdentityListResponse {
  data: ServiceIdentityItem[];
  offset: number;
  max: number;
  total: number;
  sort: ServiceIdentitySortField;
  order: ServiceIdentitySortOrder;
}

export interface ApplicationApiItem {
  securitysystem: string;
  applicationId: number;
  applicationName: string;
  applicationDisplayName: string | null;
  applicationDescription: string | null;
  applicationLogo: string | null;
  isChecked: boolean;
  accountCreationAllowed: boolean;
}

export interface ApplicationApiResponse {
  data: ApplicationApiItem[];
  success: boolean;
  totalRecords: number;
  page: {
    max: number;
    offset: number;
  };
  msg: string;
}
